import type { NotificationResponseDto } from '@/lib/models';

/**
 * Recency buckets for the drawer, newest first: Today / Yesterday / Last 7 Days
 * / Older. Items must already be newest-first (the API orders them); we keep
 * that order within each bucket and drop empty buckets.
 */
export interface RecencyBucket {
  key: 'today' | 'yesterday' | 'last7' | 'older';
  label: string;
  items: NotificationResponseDto[];
}

const BUCKET_LABELS: Record<RecencyBucket['key'], string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 Days',
  older: 'Older',
};

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function bucketFor(createdAt: string, now: Date): RecencyBucket['key'] {
  const today = startOfDay(now);
  const created = new Date(createdAt).getTime();
  const dayMs = 86_400_000;
  if (created >= today) return 'today';
  if (created >= today - dayMs) return 'yesterday';
  if (created >= today - 7 * dayMs) return 'last7';
  return 'older';
}

const ORDER: RecencyBucket['key'][] = ['today', 'yesterday', 'last7', 'older'];

/** Group newest-first notifications into non-empty recency buckets, in display order. */
export function groupByRecency(items: NotificationResponseDto[], now: Date = new Date()): RecencyBucket[] {
  const byKey = new Map<RecencyBucket['key'], NotificationResponseDto[]>();
  for (const item of items) {
    const key = bucketFor(item.createdAt, now);
    const list = byKey.get(key);
    if (list) list.push(item);
    else byKey.set(key, [item]);
  }
  return ORDER.filter((key) => byKey.has(key)).map((key) => ({
    key,
    label: BUCKET_LABELS[key],
    items: byKey.get(key)!,
  }));
}
